import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ShieldCheck, Loader2, X, AlertCircle } from 'lucide-react';
import PackageSelection from '../components/onboarding/PackageSelection';
import ProductSelection from '../components/onboarding/ProductSelection';
import ShopifyCredentials from '../components/onboarding/ShopifyCredentials';
import AdsBudgetSelection from '../components/onboarding/AdsBudgetSelection';
import EnrollAuth from '../components/onboarding/EnrollAuth';
import { toast } from '../hooks/use-toast';
import useRazorpay from '../lib/useRazorpay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STORAGE_KEY = 'dropsquadz_enroll_v4';

// 4-step enrollment funnel:
// 0 Account → 1 Package → 2 Products → 3 Shopify → 4 Ads Budget  (+Razorpay payment)
const STEP_LABELS = ['Package', 'Products', 'Shopify', 'Ads Budget'];

const Enroll = () => {
  const navigate = useNavigate();
  const { loaded: rzpLoaded } = useRazorpay();

  const existingUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_e) { return null; }
  })();
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch (_e) { return null; }
  })();

  const [user, setUser] = useState(existingUser);
  // step 0 = auth (prerequisite), steps 1-4 = 4 onboarding steps
  const [step, setStep] = useState(existingUser ? 1 : 0);
  const [storeTypes, setStoreTypes] = useState([]);
  const [storeType, setStoreType] = useState(saved?.storeType || null);
  const [selectedProducts, setSelectedProducts] = useState(saved?.selectedProducts || []);
  const [shopify, setShopify] = useState(saved?.shopify || null);
  const [adsBudget, setAdsBudget] = useState(saved?.adsBudget || null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/store-types`);
        if (Array.isArray(res.data)) setStoreTypes(res.data);
      } catch (e) { console.error('store-types error', e); }
    })();
  }, []);

  // Persist funnel progress
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ storeType, selectedProducts, shopify, adsBudget }));
    } catch (e) { console.error('storage error', e); }
  }, [storeType, selectedProducts, shopify, adsBudget]);

  const grandTotal = useMemo(() => storeType?.fee || 0, [storeType]);

  const goNext = () => { setStep((s) => Math.min(s + 1, 4)); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const goBack = () => { setStep((s) => Math.max(s - 1, existingUser ? 1 : 0)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleComplete = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.error('storage error', e); }
    navigate('/dashboard');
  };

  // -------- Razorpay payment after step 4 --------
  const handlePayment = async () => {
    if (!user?.principalId) { toast({ title: 'Not logged in', variant: 'destructive' }); return; }
    if (!storeType)         { toast({ title: 'Select a package first', variant: 'destructive' }); return; }
    if (!adsBudget)         { toast({ title: 'Select ads budget first', variant: 'destructive' }); return; }

    setPaying(true);
    setPayError('');
    try {
      // 1) Create Razorpay order (or get free-plan enroll id)
      const payload = {
        userId:   user.principalId,
        amount:   grandTotal,
        storeType,
        products: selectedProducts,
        shopify,
        adsBudget,
        name:    user.name  || '',
        email:   user.email || '',
        phone:   user.phone || '',
      };
      const { data: orderData } = await axios.post(`${API}/razorpay/create-order`, payload);

      // 2) Free plan — no payment needed
      if (orderData.is_free) {
        await axios.post(`${API}/razorpay/complete-free`, {
          enroll_id: orderData.enroll_id,
          userId:    user.principalId,
        });
        toast({ title: 'Enrollment complete! 🎉', description: 'Your store setup has started.' });
        handleComplete();
        return;
      }

      // 3) Launch Razorpay checkout
      if (!rzpLoaded || !window.Razorpay) {
        setPayError('Payment gateway loading... please retry in a moment.');
        setPaying(false);
        return;
      }

      const options = {
        key:         orderData.key,
        amount:      orderData.amount,
        currency:    'INR',
        name:        'DropSquadz',
        description: `${storeType.name} Store Setup`,
        image:       '/dropsquadz-logo.jpeg',
        order_id:    orderData.order_id,
        prefill: {
          name:    user.name  || '',
          email:   user.email || '',
          contact: user.phone || '',
        },
        theme: { color: '#2563EB' },
        modal: {
          ondismiss: () => { setPaying(false); setPayError('Payment cancelled. Try again when ready.'); },
        },
        handler: async (response) => {
          try {
            await axios.post(`${API}/razorpay/verify-payment`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_signature:  response.razorpay_signature,
              enroll_id:           orderData.enroll_id,
            });
            toast({ title: 'Payment successful! 🎉', description: 'Your store setup has started.' });
            handleComplete();
          } catch (verifyErr) {
            console.error('Razorpay verify error', verifyErr);
            setPayError('Payment verification failed. Please contact support.');
            setPaying(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error('Razorpay order error', err);
      setPayError(err?.response?.data?.detail || 'Could not initiate payment. Please try again.');
      setPaying(false);
    }
  };

  // Progress bar shows only 4 onboarding steps (step 1–4)
  const progressStep = step - 1; // 0-based index into STEP_LABELS when step>=1

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 relative overflow-x-hidden">
      {/* Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-slate-50" />
        <div className="absolute top-0 -left-20 w-96 h-96 bg-blue-300/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 -right-20 w-96 h-96 bg-indigo-300/15 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/90 border-b border-slate-200 shadow-sm">
          <div className="container mx-auto px-3 sm:px-6 py-3 flex items-center justify-between">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="enroll-home-btn">
              <img src="/dropsquadz-logo.jpeg" alt="DropSquadz"
                className="w-9 h-9 rounded-full ring-2 ring-blue-500/40 group-hover:ring-blue-500 transition-all" />
              <span className="font-bold text-blue-600 hidden sm:inline">DropSquadz</span>
            </button>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500">
              <ShieldCheck className="w-4 h-4 text-green-500" />
              <span className="hidden sm:inline">Secure Razorpay checkout</span>
              <span className="sm:hidden">Secure</span>
            </div>
          </div>

          {/* 4-step progress (only when step >= 1) */}
          {step >= 1 && (
            <div className="container mx-auto px-3 sm:px-6 pb-3">
              <div className="flex items-center gap-1 max-w-2xl mx-auto">
                {STEP_LABELS.map((label, idx) => {
                  const done    = idx < progressStep;
                  const current = idx === progressStep;
                  return (
                    <React.Fragment key={label}>
                      <div className="flex flex-col items-center flex-1 min-w-[48px]">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[11px] sm:text-xs font-bold transition-all ${
                          done    ? 'bg-green-500 text-white'
                          : current ? 'bg-blue-600 text-white scale-110 ring-4 ring-blue-100'
                          : 'bg-slate-200 text-slate-500'
                        }`}>
                          {done ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                        </div>
                        <span className={`mt-1 text-[9px] sm:text-[11px] font-medium truncate w-full text-center ${
                          current ? 'text-blue-600' : done ? 'text-green-600' : 'text-slate-400'
                        }`}>{label}</span>
                      </div>
                      {idx < STEP_LABELS.length - 1 && (
                        <div className={`h-px flex-1 mt-4 transition-colors ${
                          idx < progressStep ? 'bg-green-500' : 'bg-slate-200'
                        }`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </header>

        <main className="flex-1 container mx-auto px-3 sm:px-6 py-6 sm:py-10">
          {/* Step 0 — Auth */}
          {step === 0 && (
            <EnrollAuth onAuthed={(u) => { setUser(u); setStep(1); }} />
          )}

          {/* Step 1 — Package Selection */}
          {step === 1 && (
            <PackageSelection
              storeTypes={storeTypes}
              selected={storeType}
              onSelect={setStoreType}
              onNext={goNext}
              onBack={() => navigate('/')}
            />
          )}

          {/* Step 2 — Product Selection */}
          {step === 2 && (
            <ProductSelection
              storeType={storeType}
              selectedProducts={selectedProducts}
              onProductsChange={setSelectedProducts}
              onNext={goNext}
              onBack={goBack}
            />
          )}

          {/* Step 3 — Shopify */}
          {step === 3 && (
            <ShopifyCredentials
              credentials={shopify}
              onCredentialsChange={setShopify}
              onNext={goNext}
              onBack={goBack}
            />
          )}

          {/* Step 4 — Ads Budget + Razorpay Payment */}
          {step === 4 && (
            <div>
              <AdsBudgetSelection
                selectedProducts={selectedProducts}
                selectedBudget={adsBudget}
                planName={storeType?.name || 'Basic'}
                onBudgetChange={setAdsBudget}
                onNext={() => {/* handled by payment button below */}}
                onBack={goBack}
                hideNextButton
              />

              {/* Razorpay Payment CTA */}
              {adsBudget && (
                <div className="max-w-3xl mx-auto mt-4">
                  {/* Order summary card */}
                  <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-4 sm:p-6 mb-4">
                    <h3 className="font-bold text-slate-900 text-base sm:text-lg mb-3 flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-green-500" /> Order Summary
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Package</span>
                        <span className="font-semibold text-slate-800">{storeType?.name} Store</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Products</span>
                        <span className="font-semibold text-slate-800">{selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Shopify</span>
                        <span className="font-semibold text-slate-800 capitalize">{shopify?.mode === 'new' ? 'New store' : 'Existing store'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Ads Budget</span>
                        <span className="font-semibold text-slate-800 text-xs">
                          ₹{adsBudget?.perProduct?.toLocaleString('en-IN')}/product/day × {adsBudget?.days || 0} days
                          <span className="ml-1 text-orange-600 font-bold">(Pay after go-live)</span>
                        </span>
                      </div>
                      <div className="border-t border-slate-200 my-2" />
                      <div className="flex justify-between text-base font-bold">
                        <span className="text-slate-700">Store Setup Fee</span>
                        <span className="text-blue-700">
                          {grandTotal > 0 ? `₹${grandTotal.toLocaleString('en-IN')}` : 'FREE'}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-400">Ads Budget (later)</span>
                        <span className="text-orange-600 font-semibold">₹{(adsBudget?.totalCampaign || 0).toLocaleString('en-IN')} — Pay after store goes live</span>
                      </div>
                    </div>
                  </div>

                  {payError && (
                    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{payError}</span>
                    </div>
                  )}

                  <button
                    onClick={handlePayment}
                    disabled={paying}
                    data-testid="razorpay-pay-btn"
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:opacity-60 text-white font-extrabold text-base sm:text-lg py-4 sm:py-5 rounded-2xl shadow-xl shadow-blue-500/30 transition-all active:scale-[0.99] flex items-center justify-center gap-2"
                  >
                    {paying ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                    ) : grandTotal > 0 ? (
                      <>Pay ₹{grandTotal.toLocaleString('en-IN')} via Razorpay →</>
                    ) : (
                      <>Complete Free Enrollment →</>
                    )}
                  </button>
                  <p className="text-[11px] text-slate-400 text-center mt-2 flex items-center justify-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-green-500" />
                    {grandTotal > 0
                      ? 'Secure payment via Razorpay — UPI, Cards, NetBanking, Wallets accepted'
                      : 'Free plan — no payment needed. We start immediately!'}
                  </p>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Enroll;
